import { useConvex, useMutation, useQuery } from 'convex/react';
import { useCallback, useMemo } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import {
  createDefaultDocuments,
  type Employee,
  type EmployeeDocumentRecord,
} from '../types/employee';
import type {
  CreateEmployeeInput,
  UpdateEmployeeInput,
} from '../services/hr/employeeProfileService';
import { hashPin, isValidPin } from '../services/pinService';

/**
 * Adapts Convex employee rows to the `Employee` shape the HR UI already
 * consumes, so the ~15 components reading employees need no changes.
 *
 * The Convex document id becomes `Employee.id`. Anything still keyed by the old
 * localStorage `teacher-N` ids (shifts, punches, time-off, approvals) will not
 * resolve against these ids — that data is migrated separately, which is what
 * `employees.legacyId` exists to bridge.
 */

export function isConvexConfigured(): boolean {
  return Boolean(import.meta.env.VITE_CONVEX_URL);
}

type ConvexEmployeeRow = {
  _id: Id<'employees'>;
  legacyId?: string;
  firstName: string;
  lastName: string;
  email?: string;
  status: 'active' | 'inactive';
  pinHash?: string;
  hireDate?: string;
  centreId?: string;
  jobRole?: string;
  mfa: Employee['mfa'];
  compliance: Employee['compliance'];
  portfolio: Partial<Employee['portfolio']> & {
    tags: string[];
    managerNotes: string;
    rating: number | null;
  };
  documents: EmployeeDocumentRecord[];
  voidCheque?: Employee['voidCheque'] | null;
  inviteSent?: boolean;
  clerkUserId?: string;
};

function toEmployee(row: ConvexEmployeeRow): Employee {
  return {
    id: row._id,
    legacyId: row.legacyId,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    status: row.status,
    // Employees seeded from payroll have no kiosk PIN yet; an empty hash never
    // matches, so they simply cannot punch until a manager assigns one.
    pinHash: row.pinHash ?? '',
    hireDate: row.hireDate,
    centreId: row.centreId,
    jobRole: row.jobRole,
    mfa: row.mfa,
    compliance: row.compliance,
    portfolio: {
      tags: row.portfolio.tags ?? [],
      managerNotes: row.portfolio.managerNotes ?? '',
      rating: row.portfolio.rating ?? null,
      observation: row.portfolio.observation ?? '',
      evaluation: row.portfolio.evaluation ?? '',
      notice: row.portfolio.notice ?? '',
    },
    documents: row.documents.length > 0 ? row.documents : createDefaultDocuments(row.compliance),
    voidCheque: row.voidCheque ?? undefined,
    inviteSent: row.inviteSent ?? false,
    clerkUserId: row.clerkUserId,
  };
}

export type ConvexEmployeeApi = {
  employees: Employee[];
  /** True until the first query result arrives. */
  isLoading: boolean;
  createEmployeeProfile: (input: CreateEmployeeInput) => Promise<Employee>;
  updateEmployeeProfile: (input: UpdateEmployeeInput) => Promise<void>;
  setEmployeePin: (employeeId: string, pin: string) => Promise<boolean>;
  addAccumulatedHours: (employeeId: string, hours: number) => Promise<void>;
};

export function useConvexEmployees(): ConvexEmployeeApi {
  const convex = useConvex();
  const rows = useQuery(api.employees.listWithDocuments, {});
  const createMutation = useMutation(api.employees.create);
  const updateMutation = useMutation(api.employees.update);
  const setPinMutation = useMutation(api.employees.setPinHash);
  const addHoursMutation = useMutation(api.employees.addAccumulatedHours);

  const employees = useMemo(
    () => (rows ?? []).map((row) => toEmployee(row as ConvexEmployeeRow)),
    [rows]
  );

  const createEmployeeProfile = useCallback(
    async (input: CreateEmployeeInput): Promise<Employee> => {
      if (input.pin != null && input.pin !== '' && !isValidPin(input.pin)) {
        throw new Error('invalid-pin');
      }
      const id = await createMutation({
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email?.trim() || undefined,
        status: input.status ?? 'active',
        hireDate: input.hireDate,
        qualification: input.qualification ?? input.mfa?.qualification ?? 'qualified',
        echelonCode: input.echelonCode ?? input.mfa?.echelonCode ?? null,
        ancienneteHours: input.ancienneteHours ?? input.mfa?.ancienneteHours ?? 0,
        baseRateDisplay: input.baseRateDisplay ?? input.mfa?.baseRateDisplay ?? null,
        pinHash: input.pin && isValidPin(input.pin) ? hashPin(input.pin) : undefined,
        voidCheque: input.voidCheque ?? undefined,
      });

      // Read back rather than reconstructing, so the caller sees exactly what
      // was stored (including server-applied defaults).
      const created = await convex.query(api.employees.get, { employeeId: id });
      if (!created) throw new Error('employee-not-found');
      return toEmployee({ ...created, documents: [] } as ConvexEmployeeRow);
    },
    [createMutation, convex]
  );

  const updateEmployeeProfile = useCallback(
    async (input: UpdateEmployeeInput): Promise<void> => {
      const employeeId = input.id as Id<'employees'>;

      // `documents` is derived from the uploaded files and the compliance flags
      // are rewritten by the document mutations, so a profile update cannot set
      // either — see convex/employeeDocuments.ts.
      if (input.pin && isValidPin(input.pin)) {
        await setPinMutation({ employeeId, pinHash: hashPin(input.pin) });
      }

      // Only send fields the caller actually supplied; `update` treats
      // undefined as "leave unchanged".
      await updateMutation({
        employeeId,
        firstName: input.firstName,
        lastName: input.lastName,
        email: input.email,
        status: input.status,
        hireDate: input.hireDate,
        qualification: input.qualification ?? input.mfa?.qualification,
        echelonCode:
          input.echelonCode !== undefined ? input.echelonCode : input.mfa?.echelonCode,
        ancienneteHours: input.ancienneteHours ?? input.mfa?.ancienneteHours,
        baseRateDisplay:
          input.baseRateDisplay !== undefined
            ? input.baseRateDisplay
            : input.mfa?.baseRateDisplay,
        portfolio: input.portfolio
          ? {
              tags: input.portfolio.tags ?? [],
              managerNotes: input.portfolio.managerNotes ?? '',
              rating: input.portfolio.rating ?? null,
              observation: input.portfolio.observation ?? '',
              evaluation: input.portfolio.evaluation ?? '',
              notice: input.portfolio.notice ?? '',
            }
          : undefined,
        voidCheque: input.voidCheque,
        inviteSent: input.inviteSent,
        // Compliance is not sent: the document mutations rewrite it from the
        // files on every change, so anything written here would be transient.
      });
    },
    [updateMutation, setPinMutation]
  );

  const setEmployeePin = useCallback(
    async (employeeId: string, pin: string): Promise<boolean> => {
      if (!isValidPin(pin)) return false;
      await setPinMutation({
        employeeId: employeeId as Id<'employees'>,
        pinHash: hashPin(pin),
      });
      return true;
    },
    [setPinMutation]
  );

  const addAccumulatedHours = useCallback(
    async (employeeId: string, hours: number): Promise<void> => {
      if (hours <= 0) return;
      await addHoursMutation({ employeeId: employeeId as Id<'employees'>, hours });
    },
    [addHoursMutation]
  );

  return {
    employees,
    isLoading: rows === undefined,
    createEmployeeProfile,
    updateEmployeeProfile,
    setEmployeePin,
    addAccumulatedHours,
  };
}
